import { useState } from "react";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { useFormik } from "formik";
import * as Yup from "yup";

import { apiClient } from "src/api/client";
import closeIcon from "src/assets/icons/closeGray.svg";
import { Button, BUTTONTYPE } from "src/components/atoms/Button";
import { Input } from "src/components/atoms/Input";
import { useAppDispatch } from "src/store/hooks";
import { fetchClients } from "src/store/slices/clientsSlice";
import { closeModal } from "src/store/slices/modalSlice";
import { setSelectedClient } from "src/store/slices/selectedClientSlice";
import { getErrorMessage } from "src/utils/errors/getErrorMessage";

import styles from "./styles.module.css";

const validationSchema = Yup.object({
  first_name: Yup.string()
    .required("First name is required")
    .min(2, "First name must be at least 2 characters")
    .matches(/^[^\d]+$/, "First name must not contain numbers"),
  last_name: Yup.string()
    .required("Last name is required")
    .min(2, "Last name must be at least 2 characters")
    .matches(/^[^\d]+$/, "Last name must not contain numbers"),
  email: Yup.string()
    .required("Email is required")
    .email("Invalid email address"),
});

export const AddClient: React.FC = () => {
  const dispatch = useDispatch();
  const appDispatch = useAppDispatch();
  const navigate = useNavigate();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const formik = useFormik({
    initialValues: {
      first_name: "",
      last_name: "",
      email: "",
    },
    validationSchema,
    onSubmit: async (values, { setErrors }) => {
      setIsSubmitting(true);
      setSubmitError(null);
      try {
        const newClient = await apiClient.createClient({
          first_name: values.first_name.trim(),
          last_name: values.last_name.trim(),
          email: values.email.trim(),
          date_birth: null,
          state_id: null,
          partner: undefined,
          childrens: [],
          companies: [],
        });
        const clientsList = await appDispatch(
          fetchClients({ page: 1, page_size: 20 }),
        ).unwrap();
        const createdClient = clientsList.items.find(
          (client) => client.id === newClient.id,
        );
        if (createdClient) {
          appDispatch(
            setSelectedClient({
              id: createdClient.id,
              name: createdClient.name,
              email: createdClient.email,
              state: createdClient.state,
              avatarUrl: createdClient.avatar,
              status: "approved",
              link_id: createdClient.link_id,
            }),
          );
        }
        dispatch(closeModal({ type: "addClient" }));
        navigate(`/clients/${newClient.id}`);
      } catch (error: unknown) {
        const responseData = (
          error as { response?: { data?: Record<string, string | string[]> } }
        ).response?.data;
        const fieldErrors: Record<string, string> = {};
        ["email", "first_name", "last_name"].forEach((field) => {
          const value = responseData?.[field];
          if (value) {
            fieldErrors[field] = Array.isArray(value) ? value[0] : value;
          }
        });
        if (Object.keys(fieldErrors).length) {
          setErrors(fieldErrors);
        } else {
          setSubmitError(getErrorMessage(error));
        }
      } finally {
        setIsSubmitting(false);
      }
    },
  });

  return (
    <div className={styles.modalOverlay}>
      <div className={styles.modalContent}>
        <div className={styles.header}>
          <h3>Add new client</h3>
          <Button
            text=""
            icon={closeIcon}
            btnType={BUTTONTYPE.WITHOUT_BORDER}
            onClick={() => dispatch(closeModal({ type: "addClient" }))}
          />
        </div>

        <form onSubmit={formik.handleSubmit} className={styles.form}>
          <Input
            value={formik.values.first_name}
            label="First name"
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            name="first_name"
            error={formik.touched.first_name && formik.errors.first_name}
          />
          <Input
            value={formik.values.last_name}
            label="Last name"
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            name="last_name"
            error={formik.touched.last_name && formik.errors.last_name}
          />
          <Input
            value={formik.values.email}
            label="Email"
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            name="email"
            error={formik.touched.email && formik.errors.email}
          />
          {submitError && <div className={styles.errorText}>{submitError}</div>}
          <Button
            text={isSubmitting ? "Adding client..." : "Add client"}
            type="submit"
            disabled={!formik.isValid || !formik.dirty || isSubmitting}
          />
        </form>
      </div>
    </div>
  );
};
